import {
  Episode,
  Tier,
  useGetSubscriptionQuery,
  usePermissionsQuery,
  useShowQuery,
  useUserQuery,
} from 'src/generated/graphql';
import useFetchEpisode from 'src/hooks/useFetchEpisode';

export function useSubscriptionCache() {
  try {
    // Skip the query when no user is in cache. This prevents the
    // hook from firing getSubscription for logged-out visitors on
    // public routes (e.g. /login, /signup), which would otherwise
    // return UNAUTHENTICATED and trip the session-invalidation flow.
    const user = useUserCache();
    // Signup caches the user before it creates the subscription, and getSubscription
    // fails until then. Wait for permissions to report a subscription before asking
    // the server; meanwhile serve whatever subscription is already cached.
    const { data: permissionsCache } = usePermissionsQuery({ fetchPolicy: 'cache-only' });
    const tier = permissionsCache?.permissions?.tier;
    const fetchPolicy =
      tier && tier !== Tier.TrialSignupIncomplete ? 'cache-and-network' : 'cache-only';
    const { data: cache } = useGetSubscriptionQuery({
      fetchPolicy,
      nextFetchPolicy: fetchPolicy,
      skip: !user,
    });

    if (!cache) {
      return;
    }

    return cache.subscription;
  } catch (error) {
    throw new Error(`Cannot find subscription - ${error}`, { cause: error });
  }
}

export function useUserCache() {
  try {
    const { data: cache } = useUserQuery({ fetchPolicy: 'cache-only' });

    if (!cache) {
      return;
    }

    return cache.user;
  } catch (error) {
    throw new Error(`Cannot find user - ${error}`, { cause: error });
  }
}

export function useShowCache() {
  try {
    // NOTE: I think there is no case where userCache would be undefined when show cache is required
    // therefore it's safe to get user from cache here
    const user = useUserCache();
    const { data: cache } = useShowQuery({
      fetchPolicy: 'cache-only',
      variables: { id: user?.activeProjectId ?? 'none' },
    });

    if (!cache) {
      return;
    }

    return cache.show;
  } catch (error) {
    throw new Error(`Cannot find show - ${error}`, { cause: error });
  }
}

/**
 *
 * @module useEpisodeBuilderCache
 * This hook is can be used in any child component of the episode builder component.
 */
export function useEpisodeBuilderCache(): Omit<Episode, 'logs'> {
  try {
    const { episode } = useFetchEpisode();
    if (!episode) {
      throw new Error();
    }
    return { ...episode, timeline: episode.timeline ?? [] };
  } catch (error) {
    throw new Error(`Cannot find episode details - ${error}`, { cause: error });
  }
}
